import React, { useEffect, useState } from "react";
import { Modal, Table, Button, Form, Select, InputNumber, message, Tabs, Tag, Avatar } from "antd";
import { PlusOutlined, DeleteOutlined, UserOutlined } from "@ant-design/icons";
import { GetCazaRecompenses, PostCazaRecompensa, DeleteCazaRecompensa, GetCazaUsuarisRecompenses } from "../../../../dao/jutge/CazaRecompensesDao";
import { GetJavamonConfig, GetSkins } from "../../../../dao/jutge/GetJavamonConfig";
import { GetShopItems } from "../../../../dao/jutge/GetShopItems";

const { Option } = Select;
const { TabPane } = Tabs;
const genericUrl = process.env.REACT_APP_API_URL;

function getAvatarSrc(img: string | undefined | null) {
    if (!img || img === "undefined" || img === "null") return undefined;
    if (img === "avatar1.jpg" || img === "/images/avatar1.jpg") return "/images/avatar1.jpg";
    if (img.startsWith("http") || img.startsWith("data:") || img.startsWith("/")) return img;
    if (img.startsWith("base64:")) img = img.split(":").pop()!;
    const clean = img.replace(/\s/g, "");
    if (!clean) return undefined;
    if (clean.startsWith("/9j/")) return `data:image/jpeg;base64,${clean}`;
    if (clean.startsWith("iVB")) return `data:image/png;base64,${clean}`;
    return `data:image/png;base64,${clean}`;
}

const stringToColor = (str: string) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    const c = (hash & 0x00FFFFFF).toString(16).toUpperCase();
    return '#' + '00000'.substring(0, 6 - c.length) + c;
};

interface Props {
    caza: any; // EscenarioCaza { id, nombre, ... }
    idConjunt?: number | string;
    visible: boolean;
    onClose: (hasChanges?: boolean) => void;
}

/**
 * Modal para gestionar las múltiples recompensas de una Píldora de Aprendizaje (Caza),
 * idéntico al selector de recompensas de ActivitatsIdExercicis (PremiosModal) y BotinRecompensesModal.
 */
const PildoraRecompensesModal: React.FC<Props> = ({ caza, idConjunt, visible, onClose }) => {
    const [loading, setLoading] = useState(false);
    const [recompenses, setRecompenses] = useState<any[]>([]);
    const [historial, setHistorial] = useState<any[]>([]);
    const [tipoSeleccionado, setTipoSeleccionado] = useState<string>('');
    const [hasChanges, setHasChanges] = useState<boolean>(false);

    // Datos de configuración para poblar los selectores de items
    const [javamons, setJavamons] = useState<any[]>([]);
    const [seasons, setSeasons] = useState<any[]>([]);
    const [shopItems, setShopItems] = useState<any[]>([]);
    const [skinsData, setSkinsData] = useState<any>({});

    const [form] = Form.useForm();

    useEffect(() => {
        if (visible && caza) {
            setHasChanges(false);
            loadRecompenses();
            loadConfigData();
            loadHistorial();
        }
    }, [visible, caza]);

    async function loadRecompenses() {
        setLoading(true);
        const data = await GetCazaRecompenses(caza.id, idConjunt);
        setRecompenses(data || []);
        setLoading(false);
    }

    async function loadHistorial() {
        const data = await GetCazaUsuarisRecompenses(caza.id, idConjunt);
        setHistorial(Array.isArray(data) ? data : []);
    }

    async function loadConfigData() {
        const jConfig = await GetJavamonConfig();
        if (jConfig) {
            const parse = (v: any) => (typeof v === 'string' && v && v !== 'null' ? JSON.parse(v) : (Array.isArray(v) ? v : []));
            setJavamons(jConfig.javamons ? parse(jConfig.javamons) : []);
            setSeasons(jConfig.seasons ? parse(jConfig.seasons) : []);
        }
        const sData = await GetSkins();
        if (sData) setSkinsData(sData);
        const shopData = await GetShopItems("0");
        if (shopData) setShopItems(shopData);
    }

    async function handleAddRecompensa(values: any) {
        setLoading(true);
        const ok = await PostCazaRecompensa(caza.id, values);
        if (ok) {
            message.success("Recompensa añadida a la píldora");
            setHasChanges(true);
            form.resetFields();
            setTipoSeleccionado('');
            await loadRecompenses();
        } else {
            message.error("Error al añadir la recompensa");
        }
        setLoading(false);
    }

    async function handleDeleteRecompensa(id: string | number) {
        setLoading(true);
        const ok = await DeleteCazaRecompensa(caza.id, id);
        if (ok) {
            message.success("Recompensa eliminada");
            setHasChanges(true);
            await loadRecompenses();
        } else {
            message.error("Error al eliminar la recompensa");
        }
        setLoading(false);
    }

    // Tipos que NO requieren seleccionar un item concreto
    const noItemTypes = ["PUNTUACION", "JAVAMON_RANDOM"];
    const isSeasonType = tipoSeleccionado === "JAVAMON_SEASON";

    function itemListForType(tipo: string): any[] {
        if (tipo === "JAVAMON") return javamons;
        if (tipo === "SHOP_ITEM") return shopItems;
        if (tipo === "SKIN") return skinsData.skins || [];
        if (tipo === "ARMA") return skinsData.armes || [];
        if (tipo === "ESCUDO") return skinsData.escuts || [];
        if (tipo === "ARCO") return skinsData.arcs || [];
        if (tipo === "FLECHA") return skinsData.flechas || [];
        if (tipo === "MAGIA") return skinsData.magias || [];
        if (tipo === "AVATAR") return skinsData.avatars || [];
        return [];
    }

    function imgUrlForItem(tipo: string, item: any): string | null {
        if (tipo === "JAVAMON") return `${genericUrl}:8061/jutge/game/image/javamon/${item.id}`;
        if (tipo === "SHOP_ITEM") {
            if (item.image && item.image.startsWith('data:')) return item.image;
            if (item.image) return `data:image/png;base64,${item.image}`;
            return `${genericUrl}:8061/jutge/game/image/shop/${item.id}`;
        }
        if (item.imagen && item.imagen.startsWith('data:')) return item.imagen;
        if (item.imagen) return `data:image/png;base64,${item.imagen}`;
        let seg = tipo.toLowerCase();
        if (seg === "escudo") seg = "escut";
        if (seg === "arco") seg = "arc";
        return `${genericUrl}:8061/jutge/game/image/${seg}/${item.id}`;
    }

    const columns = [
        { title: "Tipo", dataIndex: "tipo", key: "tipo" },
        { title: "Valor", dataIndex: "valor", key: "valor", render: (v: any) => v || '-' },
        { title: "Límite", dataIndex: "limit_quantity", key: "limit_quantity", render: (v: any) => v || 'Infinito' },
        { title: "Int. Mín", dataIndex: "intentos_min", key: "intentos_min", render: (v: any) => v || 1 },
        { title: "Int. Máx", dataIndex: "intentos_max", key: "intentos_max", render: (v: any) => v || 'Infinito' },
        { title: "Tiempo (min)", dataIndex: "time_limit", key: "time_limit", render: (v: any) => v || 'Sin límite' },
        { title: "Entregadas", dataIndex: "claimed_count", key: "claimed_count", render: (v: any) => v || 0 },
        { 
            title: "Modalidad", 
            dataIndex: "grupo_seleccion",
            key: "grupo_seleccion",
            render: (g: any) => {
                const grp = Number(g) || 0;
                if (grp > 0) {
                    return <Tag color="blue">Elección (Grupo {grp})</Tag>;
                }
                return <Tag color="green">Automática</Tag>;
            }
        },
        {
            title: "Item",
            key: "item",
            render: (r: any) => {
                if (!r.id_item || noItemTypes.includes(r.tipo)) return '-';
                if (r.tipo === "JAVAMON_SEASON") {
                    const s = seasons.find((x: any) => x.id + "" === r.id_item + "");
                    return s ? `Temporada: ${s.nom}` : `Temporada #${r.id_item}`;
                }
                const list = itemListForType(r.tipo);
                const item = list.find((x: any) => x.id + "" === r.id_item + "");
                const name = item ? (item.nom || item.name) : `ID: ${r.id_item}`;
                const url = item ? imgUrlForItem(r.tipo, item) : null;
                return (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        {url && <img src={url} alt="" style={{ width: 24, height: 24, marginRight: 8, objectFit: 'contain' }} />}
                        {name}
                    </div>
                );
            }
        },
        {
            title: "Acciones",
            key: "acciones",
            render: (r: any) => (
                <Button type="text" danger icon={<DeleteOutlined />} onClick={() => handleDeleteRecompensa(r.id)} />
            )
        }
    ];

    const historialColumns = [
        {
            title: "Alumno",
            key: "alumno",
            render: (r: any) => {
                const fullName = `${r.nom || ''} ${r.cognoms || ''}`.trim() || `#${r.id_usuari}`;
                const initials = ((r.nom?.[0] || '') + (r.cognoms?.[0] || '')).toUpperCase() || 'U';
                const avatarSrc = getAvatarSrc(r.avatar);
                const avatarColor = stringToColor(fullName);

                return (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar
                            size={32}
                            src={avatarSrc}
                            style={{
                                backgroundColor: avatarSrc ? undefined : avatarColor,
                                color: '#fff',
                                fontWeight: 'bold',
                                fontSize: 13,
                                flexShrink: 0,
                                border: '1px solid #f0f0f0',
                                marginRight: 10
                            }}
                        >
                            {!avatarSrc && initials}
                        </Avatar>
                        <span style={{ fontWeight: 500 }}>{fullName}</span>
                    </div>
                );
            }
        },
        {
            title: "Recompensa",
            key: "recompensa",
            render: (r: any) => {
                let rewardsList: any[] = [];
                try {
                    rewardsList = typeof r.rewards_json === 'string' ? JSON.parse(r.rewards_json) : (r.rewards_json || []);
                } catch {
                    rewardsList = [];
                }
                if (rewardsList.length > 0) {
                    return (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                            {rewardsList.map((rw: any, idx: number) => {
                                if (rw.tipo === 'PUNTUACION') return <Tag key={idx} color="gold">{rw.valor} punts</Tag>;
                                return <Tag key={idx} color="purple">{rw.tipo}{rw.id_item ? ` #${rw.id_item}` : ''}</Tag>;
                            })}
                        </div>
                    );
                }
                if (r.id_javamon) return <Tag color="purple">Javamon #{r.id_javamon}</Tag>;
                return <Tag color="green">Reclamada</Tag>;
            }
        },
        {
            title: "Estado",
            key: "estado",
            render: (r: any) => {
                if (!r.estado_javamon) return <Tag color="blue">Completado</Tag>;
                return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <div>
                            {r.estado_javamon === 'CAPTURADO'
                                ? <Tag color="green">Capturado</Tag>
                                : <Tag color="red">Escapado</Tag>}
                        </div>
                        {r.javaball_lanzada ? (
                            <span style={{ fontSize: 11, color: '#595959' }}>
                                🎯 <b>{r.javaball_lanzada}</b>
                                {r.javaball_origen ? ` (${r.javaball_origen === 'GAME' ? 'Juego' : 'Web'})` : ''}
                            </span>
                        ) : (
                            <span style={{ fontSize: 11, color: '#8c8c8c' }}>Sin lanzar Javaball</span>
                        )}
                    </div>
                );
            }
        }
    ];

    return (
        <Modal
            title={`🎁 Recompensas de la Píldora: ${caza?.nombre || caza?.nom || ''}`}
            open={visible}
            onCancel={() => onClose(hasChanges)}
            footer={null}
            width={950}
            destroyOnClose
        >
            <Tabs defaultActiveKey="recompenses">
                <TabPane tab="Recompensas" key="recompenses">
                    <Form form={form} layout="inline" onFinish={handleAddRecompensa} style={{ marginBottom: 20, rowGap: 12 }}>
                        <Form.Item name="tipo" rules={[{ required: true, message: 'Tipo necesario' }]}>
                            <Select
                                placeholder="Tipo"
                                style={{ width: 180 }}
                                onChange={(v) => {
                                    setTipoSeleccionado(v);
                                    form.setFieldsValue({ id_item: undefined });
                                }}
                            >
                                <Option value="PUNTUACION">Puntuación</Option>
                                <Option value="JAVAMON_RANDOM">Javamon (Aleatorio)</Option>
                                <Option value="JAVAMON">Javamon Específico</Option>
                                <Option value="JAVAMON_SEASON">Javamon de Temporada</Option>
                                <Option value="SHOP_ITEM">Objeto de Tienda</Option>
                                <Option value="SKIN">Skin</Option>
                                <Option value="ARMA">Arma</Option>
                                <Option value="ESCUDO">Escudo</Option>
                                <Option value="ARCO">Arco</Option>
                                <Option value="FLECHA">Flecha</Option>
                                <Option value="MAGIA">Magia</Option>
                                <Option value="AVATAR">Avatar</Option>
                            </Select>
                        </Form.Item>

                        <Form.Item name="valor" tooltip="Cantidad de puntos (solo Puntuación)">
                            <InputNumber placeholder="Puntos" disabled={tipoSeleccionado !== 'PUNTUACION'} />
                        </Form.Item>

                        {isSeasonType ? (
                            <Form.Item name="id_item" rules={[{ required: true, message: 'Temporada necesaria' }]} tooltip="Temporada de la que saldrá el javamon">
                                <Select placeholder="Selecciona Temporada" style={{ width: 220 }} showSearch optionFilterProp="children">
                                    {seasons.map((s: any) => (
                                        <Option key={s.id} value={s.id}>{s.nom}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        ) : (tipoSeleccionado && !noItemTypes.includes(tipoSeleccionado)) ? (
                            <Form.Item name="id_item" rules={[{ required: true, message: 'Item necesario' }]} tooltip="Selecciona el item">
                                <Select placeholder="Selecciona Item" style={{ width: 240 }} showSearch optionFilterProp="label">
                                    {itemListForType(tipoSeleccionado).map((item: any) => {
                                        const url = imgUrlForItem(tipoSeleccionado, item);
                                        return (
                                            <Option key={item.id} value={item.id} label={item.nom || item.name}>
                                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                                    {url && <img src={url} alt="" style={{ width: 20, height: 20, marginRight: 8, objectFit: 'contain' }} />}
                                                    {item.nom || item.name || `Item ${item.id}`}
                                                </div>
                                            </Option>
                                        );
                                    })}
                                </Select>
                            </Form.Item>
                        ) : (
                            <Form.Item name="id_item" hidden><InputNumber /></Form.Item>
                        )}

                        <Form.Item name="limit_quantity" tooltip="Máximo de unidades repartibles entre todos los alumnos (vacío = infinito)">
                            <InputNumber placeholder="Límite Unid." min={1} style={{ width: 110 }} />
                        </Form.Item>

                        <Form.Item name="intentos_min" initialValue={1} tooltip="Mínimo de intentos para poder conseguirlo">
                            <InputNumber placeholder="Int. Mín" min={1} style={{ width: 90 }} />
                        </Form.Item>

                        <Form.Item name="intentos_max" tooltip="Máximo de intentos permitidos (vacío = infinito)">
                            <InputNumber placeholder="Int. Máx" min={1} style={{ width: 90 }} />
                        </Form.Item>

                        <Form.Item name="time_limit" tooltip="Tiempo máximo en minutos para conseguirlo (vacío = infinito)">
                            <InputNumber placeholder="Tiempo (min)" min={1} style={{ width: 110 }} />
                        </Form.Item>

                        <Form.Item name="grupo_seleccion" initialValue={0} tooltip="0 = Automática (se obtiene directamente). 1, 2, ... = Multiopción (el alumno debe elegir SOLO UNA dentro del mismo grupo)">
                            <InputNumber placeholder="Grupo Sel. (0 = Auto)" min={0} style={{ width: 140 }} />
                        </Form.Item>

                        <Form.Item>
                            <Button type="primary" htmlType="submit" icon={<PlusOutlined />} loading={loading}>Añadir</Button>
                        </Form.Item>
                    </Form>

                    <Table rowKey="id" dataSource={recompenses} columns={columns} loading={loading} pagination={false} />
                </TabPane>

                <TabPane tab={`Historial de entregas${historial.length ? ` (${historial.length})` : ''}`} key="historial">
                    <Table
                        rowKey={(r) => r.id_usuari + '_' + (r.id || Math.random())}
                        dataSource={historial}
                        loading={loading}
                        pagination={{ pageSize: 10 }}
                        columns={historialColumns}
                    />
                </TabPane>
            </Tabs>
        </Modal>
    );
};

export default PildoraRecompensesModal;
